/**
 * Bread Catalog Service — local wrapper for the ordering-website.
 *
 * Re-implements the shared service functions using the app's own
 * Firestore/Storage instances (import { db, storage } from firebase).
 * This avoids bundler issues with the shared/ monorepo package.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  writeBatch,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { db, storage, auth } from '../firebase/firebase';

// ── Constants ──────────────────────────────────────────────────────
const COLLECTION = 'bread_catalog';
const CONFIG_COLLECTION = 'bread_catalog_config';
const CONFIG_DOC_ID = 'categories';
const AUDIT_SUBCOLLECTION = 'audit_log';
const STORAGE_PATH = 'bread_catalog';

// ── Helpers ────────────────────────────────────────────────────────

export function generateSlug(nameEs) {
  return nameEs
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function computeDiff(current, updates) {
  const changes = {};
  for (const key of Object.keys(updates)) {
    if (['updated_at', 'updated_by'].includes(key)) continue;
    const oldStr = JSON.stringify(current[key] ?? null);
    const newStr = JSON.stringify(updates[key] ?? null);
    if (oldStr !== newStr) {
      changes[key] = { old: current[key] ?? null, new: updates[key] ?? null };
    }
  }
  return changes;
}

function docToBreadItem(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    name_es: data.name_es,
    name_en: data.name_en,
    slug: data.slug,
    plu: data.plu ?? null,
    category: data.category,
    image_url: data.image_url ?? null,
    enabled: data.enabled,
    disabled_store_ids: data.disabled_store_ids ?? [],
    description_es: data.description_es ?? null,
    description_en: data.description_en ?? null,
    min_qty: data.min_qty ?? null,
    tags: data.tags ?? [],
    created_at: data.created_at,
    created_by: data.created_by,
    updated_at: data.updated_at,
    updated_by: data.updated_by,
  };
}

function docToAuditEntry(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    timestamp: data.timestamp,
    user_id: data.user_id,
    user_email: data.user_email,
    user_role: data.user_role,
    action: data.action,
    changes: data.changes ?? {},
    metadata: data.metadata ?? null,
  };
}

/** Build an AuthUser from the current Firebase auth state + optional role override. */
function getAuthUser(roleOverride) {
  const user = auth.currentUser;
  if (!user) throw new Error('No hay sesión activa');
  return {
    uid: user.uid,
    email: user.email || 'unknown',
    role: roleOverride || 'staff',
  };
}

// ── Category Config ────────────────────────────────────────────────

export async function getCategoryMeta() {
  const docSnap = await getDoc(doc(db, CONFIG_COLLECTION, CONFIG_DOC_ID));
  if (!docSnap.exists()) return {};
  const data = docSnap.data();
  return data.category_meta ?? {};
}

export async function setCategoryMeta(categoryKey, meta) {
  const user = getAuthUser();
  const configRef = doc(db, CONFIG_COLLECTION, CONFIG_DOC_ID);
  const docSnap = await getDoc(configRef);

  const existing = docSnap.exists()
    ? (docSnap.data().category_meta ?? {})
    : {};

  existing[categoryKey] = meta;

  await setDoc(configRef, {
    category_meta: existing,
    updated_at: serverTimestamp(),
    updated_by: user.uid,
  });
}

// ── Read Operations ────────────────────────────────────────────────

export async function getFullCatalog() {
  const snapshot = await getDocs(collection(db, COLLECTION));
  return snapshot.docs.map(docToBreadItem);
}

export async function getBread(breadId) {
  const docSnap = await getDoc(doc(db, COLLECTION, breadId));
  if (!docSnap.exists()) return null;
  return docToBreadItem(docSnap);
}

/**
 * Subscribe to the full catalog in real-time.
 * @param {function} callback - receives (items[])
 * @returns {function} unsubscribe
 */
export function onFullCatalog(callback) {
  return onSnapshot(collection(db, COLLECTION), (snapshot) => {
    callback(snapshot.docs.map(docToBreadItem));
  });
}

// ── Write Operations ───────────────────────────────────────────────

export async function createBread(data) {
  const user = getAuthUser();
  const batch = writeBatch(db);
  const breadRef = doc(collection(db, COLLECTION));

  const slug = generateSlug(data.name_es);
  const now = serverTimestamp();

  const breadData = {
    name_es: data.name_es,
    name_en: data.name_en,
    slug,
    plu: data.plu ?? null,
    category: data.category,
    image_url: data.image_url ?? '',
    enabled: data.enabled ?? true,
    disabled_store_ids: data.disabled_store_ids ?? [],
    description_es: data.description_es ?? null,
    description_en: data.description_en ?? null,
    min_qty: data.min_qty ?? null,
    tags: data.tags ?? [],
    created_at: now,
    created_by: user.uid,
    updated_at: now,
    updated_by: user.uid,
  };

  batch.set(breadRef, breadData);

  const auditRef = doc(collection(breadRef, AUDIT_SUBCOLLECTION));
  batch.set(auditRef, {
    timestamp: now,
    user_id: user.uid,
    user_email: user.email,
    user_role: user.role,
    action: 'created',
    changes: {},
    metadata: null,
  });

  await batch.commit();
  return breadRef.id;
}

export async function updateBread(breadId, updates) {
  const user = getAuthUser();
  const breadRef = doc(db, COLLECTION, breadId);
  const currentSnap = await getDoc(breadRef);

  if (!currentSnap.exists()) {
    throw new Error(`Pan ${breadId} no encontrado`);
  }

  const currentData = currentSnap.data();

  const updateData = { ...updates };
  if (updates.name_es && updates.name_es !== currentData.name_es) {
    updateData.slug = generateSlug(updates.name_es);
  }

  const changes = computeDiff(currentData, updateData);
  if (Object.keys(changes).length === 0) return; // no-op

  const batch = writeBatch(db);

  batch.update(breadRef, {
    ...updateData,
    updated_at: serverTimestamp(),
    updated_by: user.uid,
  });

  const auditRef = doc(collection(breadRef, AUDIT_SUBCOLLECTION));
  batch.set(auditRef, {
    timestamp: serverTimestamp(),
    user_id: user.uid,
    user_email: user.email,
    user_role: user.role,
    action: 'updated',
    changes,
    metadata: null,
  });

  await batch.commit();
}

export async function disableBread(breadId) {
  return updateBread(breadId, { enabled: false });
}

export async function enableBread(breadId) {
  return updateBread(breadId, { enabled: true });
}

// ── Audit ──────────────────────────────────────────────────────────

export async function getBreadAuditLog(breadId, limit = 50) {
  const auditRef = collection(db, COLLECTION, breadId, AUDIT_SUBCOLLECTION);
  const q = query(auditRef, orderBy('timestamp', 'desc'), firestoreLimit(limit));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToAuditEntry);
}

// ── Image Upload ───────────────────────────────────────────────────

export async function uploadBreadImage(breadId, file) {
  const ext = file.name.split('.').pop() || 'jpg';
  const fileName = `${Date.now()}.${ext}`;
  const storageRef = ref(storage, `${STORAGE_PATH}/${breadId}/${fileName}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}
